import { getApiErrorMessage } from '@/lib/utils';
import { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, Eye, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';
import type { Node, Edge } from 'reactflow';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import TenantFlowCanvas from '@/components/dialplan-flow/TenantFlowCanvas';

// Metadata contesto dialplan del tenant (GET /dialplan/context). Shape
// permissiva: solo i campi consumati dalla UI sono noti.
interface DialplanContextMeta {
  context_name: string;
  [key: string]: unknown;
}

export default function TenantContextEditor() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('flow');

  // Context data
  const [contextData, setContextData] = useState<DialplanContextMeta | null>(null);
  const [baseTemplate, setBaseTemplate] = useState('');
  const [manualOverrides, setManualOverrides] = useState('');
  const [finalXML, setFinalXML] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Flow data
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  const loadContextData = useCallback(async () => {
    setLoading(true);
    try {
      // Load context metadata
      const contextRes = await apiClient.get('/dialplan/context');
      setContextData((contextRes.data as DialplanContextMeta | undefined) ?? null);

      // Load template and overrides
      const templateRes = await apiClient.get<{ template?: string; manualOverrides?: string; finalXML?: string }>('/dialplan/context/template');
      const tplData = templateRes.data;
      setBaseTemplate(tplData?.template || '');
      setManualOverrides(tplData?.manualOverrides || '');
      setFinalXML(tplData?.finalXML || '');

      // Load flow data
      const flowRes = await apiClient.get<{ nodes?: Node[]; edges?: Edge[] }>('/dialplan/context/flow');
      const flowData = flowRes.data;
      setNodes(flowData?.nodes || []);
      setEdges(flowData?.edges || []);

      console.log('📊 [TenantContextEditor] Loaded context:', {
        context: contextRes.data,
        template_length: tplData?.template?.length,
        overrides_length: tplData?.manualOverrides?.length,
        nodes_count: flowData?.nodes?.length,
        edges_count: flowData?.edges?.length
      });
    } catch (error) {
      console.error('❌ [TenantContextEditor] Load failed:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error) || 'Failed to load context',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadContextData();
  }, [loadContextData]);

  const handleSaveOverrides = async () => {
    setSaving(true);
    try {
      await apiClient.put('/dialplan/context/overrides', {
        overrides: manualOverrides
      });

      // Reload final preview
      const templateRes = await apiClient.get<{ finalXML?: string }>('/dialplan/context/template');
      setFinalXML((templateRes.data)?.finalXML || '');

      setHasChanges(false);
      toast({
        title: 'Success',
        description: 'Manual overrides saved successfully',
      });
    } catch (error) {
      console.error('❌ [TenantContextEditor] Save failed:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error) || 'Failed to save overrides',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFlow = async (updatedNodes: Node[], updatedEdges: Edge[]) => {
    setSaving(true);
    try {
      await apiClient.post('/dialplan/context/nodes', {
        nodes: updatedNodes,
        edges: updatedEdges
      });

      setNodes(updatedNodes);
      setEdges(updatedEdges);

      // Reload final preview
      const templateRes = await apiClient.get<{ finalXML?: string }>('/dialplan/context/template');
      setFinalXML((templateRes.data)?.finalXML || '');

      toast({
        title: 'Success',
        description: 'Flow diagram saved successfully',
      });
    } catch (error) {
      console.error('❌ [TenantContextEditor] Save flow failed:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error) || 'Failed to save flow',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    setSaving(true);
    try {
      await apiClient.post('/dialplan/context/deploy');

      toast({
        title: 'Success',
        description: 'Context deployed to FreeSWITCH successfully',
      });
    } catch (error) {
      console.error('❌ [TenantContextEditor] Deploy failed:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error) || 'Failed to deploy context',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contextData) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No context found for this tenant. Please contact support.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Context Editor</h1>
          <p className="text-muted-foreground mt-1">
            Manage your tenant dialplan context: {contextData.context_name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadContextData}
            disabled={saving}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleDeploy}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Deploy to FreeSWITCH
              </>
            )}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dialplan Context Editor</CardTitle>
          <CardDescription>
            Visual editor for your tenant dialplan. Drag nodes from the library to build your call flow.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="flow">Flow Diagram</TabsTrigger>
              <TabsTrigger value="template">Base Template</TabsTrigger>
              <TabsTrigger value="overrides">Manual Overrides</TabsTrigger>
              <TabsTrigger value="preview">Final Preview</TabsTrigger>
            </TabsList>

            <TabsContent value="flow" className="space-y-4">
              <Alert>
                <Eye className="h-4 w-4" />
                <AlertDescription>
                  <strong>Interactive Flow Editor:</strong> Drag nodes from the library onto the canvas to build your dialplan.
                  Connect nodes to define call routing logic. Changes are saved automatically.
                </AlertDescription>
              </Alert>

              <div className="border rounded-lg" style={{ height: '600px' }}>
                <TenantFlowCanvas
                  initialNodes={nodes}
                  initialEdges={edges}
                  onSave={handleSaveFlow}
                  contextName={contextData.context_name}
                />
              </div>
            </TabsContent>

            <TabsContent value="template" className="space-y-4">
              <Alert>
                <Eye className="h-4 w-4" />
                <AlertDescription>
                  <strong>Read-Only:</strong> This is the base template generated from your extensions, routes, and time conditions.
                  To modify, use the Flow Diagram or edit your routes/extensions in their respective pages.
                </AlertDescription>
              </Alert>

              <Textarea
                value={baseTemplate}
                readOnly
                className="font-mono text-xs h-[500px]"
                placeholder="Base template will appear here..."
              />
            </TabsContent>

            <TabsContent value="overrides" className="space-y-4">
              <Alert>
                <AlertDescription>
                  <strong>Advanced:</strong> Add custom XML dialplan rules here. These will be merged with the base template.
                  Use with caution - invalid XML will break your dialplan.
                </AlertDescription>
              </Alert>

              <Textarea
                value={manualOverrides}
                onChange={(e) => {
                  setManualOverrides(e.target.value);
                  setHasChanges(true);
                }}
                className="font-mono text-xs h-[450px]"
                placeholder="<!-- Add custom XML dialplan rules here -->"
              />

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveOverrides}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Overrides
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="space-y-4">
              <Alert>
                <Eye className="h-4 w-4" />
                <AlertDescription>
                  <strong>Final Preview:</strong> This is the complete XML that will be deployed to FreeSWITCH.
                  It includes the base template + manual overrides.
                </AlertDescription>
              </Alert>

              <Textarea
                value={finalXML}
                readOnly
                className="font-mono text-xs h-[500px]"
                placeholder="Final XML preview will appear here..."
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

